import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { AUTHENTICATION_FAILED_SILENT } from '../../actions';
import { dismissNotificationPopUp, showInfoPopup } from '../../actions/notification-actions';
import {
    REAUTH_MODAL_MESSAGE,
    REAUTH_MODAL_TITLE,
    REAUTH_REASON_SESSION_REVOKED,
    REAUTH_REDIRECT_DELAY_MS,
    REAUTH_REQUIRED_EVENT,
    SESSION_REVOKED_CONFIRM_LABEL,
    SESSION_REVOKED_MODAL_MESSAGE,
    SESSION_REVOKED_MODAL_TITLE,
    clearReauthSession,
    getPendingReauthReason,
    isReauthPending,
    redirectToLogin,
} from '../../utils/reauth';

export const useReauthListener = () => {
    const dispatch = useDispatch();
    const handledReasonRef = useRef(undefined);

    // A revoked device still owns a valid, paid account, so it drops to an ordinary signed-out visitor
    // and stays on the page instead of being marched to /login — an anonymous browser can read this page,
    // and before this the eviction was indistinguishable from account deletion.
    const handleSessionRevoked = useCallback(() => {
        clearReauthSession();
        dispatch({ type: AUTHENTICATION_FAILED_SILENT, message: null });
        dispatch(showInfoPopup({
            title: SESSION_REVOKED_MODAL_TITLE,
            message: SESSION_REVOKED_MODAL_MESSAGE,
            confirmButtonLabel: SESSION_REVOKED_CONFIRM_LABEL,
            showConfirmButton: true,
            showCancelButton: false,
            showCloseButton: true,
        }));
    }, [dispatch]);

    const handleAccountDeleted = useCallback(() => {
        dispatch(showInfoPopup({
            title: REAUTH_MODAL_TITLE,
            message: REAUTH_MODAL_MESSAGE,
            showConfirmButton: false,
            showCancelButton: false,
            showCloseButton: false,
        }));

        clearReauthSession();

        setTimeout(() => {
            dispatch(dismissNotificationPopUp());
            redirectToLogin();
        }, REAUTH_REDIRECT_DELAY_MS);
    }, [dispatch]);

    useEffect(() => {
        const handleReauthRequired = (event) => {
            const reason = event?.detail?.reason ?? getPendingReauthReason();

            // Keyed on the reason rather than handled-once: the session_revoked branch does not navigate
            // away, so a plain one-shot would swallow a later, genuine account_deleted for the rest of the
            // page session. Repeats of the same reason are still collapsed, which is what the burst of
            // concurrent 401s from a single revocation produces.
            if (handledReasonRef.current === reason) {
                return;
            }
            handledReasonRef.current = reason;

            // An unknown or absent reason keeps the deleted-account treatment: forcing a re-login is the
            // safer default, and a new server-side reason can never silently become a no-op.
            if (reason === REAUTH_REASON_SESSION_REVOKED) {
                handleSessionRevoked();
                return;
            }

            handleAccountDeleted();
        };

        window.addEventListener(REAUTH_REQUIRED_EVENT, handleReauthRequired);
        if (isReauthPending()) {
            handleReauthRequired();
        }
        return () => {
            window.removeEventListener(REAUTH_REQUIRED_EVENT, handleReauthRequired);
        };
    }, [handleAccountDeleted, handleSessionRevoked]);
};
